/**
 * CodeMirror 6 wrapper for SDF shader editing
 */

import { EditorView, basicSetup } from 'codemirror';
import { EditorState, StateField, StateEffect, Compartment } from '@codemirror/state';
import { keymap, Decoration } from '@codemirror/view';
import { cpp } from '@codemirror/lang-cpp';
import { oneDark } from '@codemirror/theme-one-dark';
import { indentWithTab } from '@codemirror/commands';
import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import { tags } from '@lezer/highlight';

// Custom light theme
const lightTheme = EditorView.theme({
  '&': {
    backgroundColor: '#ffffff',
    color: '#1a1a1a',
  },
  '.cm-content': {
    caretColor: '#1a1a1a',
  },
  '.cm-cursor': {
    borderLeftColor: '#1a1a1a',
  },
  '.cm-selectionBackground, ::selection': {
    backgroundColor: '#b3d4fc',
  },
  '.cm-gutters': {
    backgroundColor: '#f5f5f5',
    color: '#6e7681',
    borderRight: '1px solid #e0e0e0',
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#e8e8e8',
  },
  '.cm-activeLine': {
    backgroundColor: '#f5f5f5',
  },
  '.cm-line': {
    color: '#1a1a1a',
  },
}, { dark: false });

// Light theme syntax highlighting
const lightHighlightStyle = HighlightStyle.define([
  { tag: tags.keyword, color: '#d73a49' },
  { tag: tags.comment, color: '#6a737d', fontStyle: 'italic' },
  { tag: tags.string, color: '#032f62' },
  { tag: tags.number, color: '#005cc5' },
  { tag: tags.operator, color: '#d73a49' },
  { tag: tags.function(tags.variableName), color: '#6f42c1' },
  { tag: tags.typeName, color: '#22863a' },
  { tag: tags.className, color: '#6f42c1' },
  { tag: tags.definition(tags.variableName), color: '#005cc5' },
  { tag: tags.propertyName, color: '#005cc5' },
]);

// Available editor themes
export const EDITOR_THEMES = {
  oneDark: { name: 'One Dark', extension: oneDark },
  light: { name: 'Light', extension: [lightTheme, syntaxHighlighting(lightHighlightStyle)] },
};

// Effect to set error lines
const setErrorLinesEffect = StateEffect.define();

// Error line decoration
const errorLineMark = Decoration.line({ class: 'cm-error-line' });

// StateField to track error lines
const errorLinesField = StateField.define({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setErrorLinesEffect)) {
        const lines = effect.value;
        if (lines.length === 0) {
          return Decoration.none;
        }
        const doc = tr.state.doc;
        const marks = [];
        for (const lineNum of lines) {
          if (lineNum >= 1 && lineNum <= doc.lines) {
            const line = doc.line(lineNum);
            marks.push(errorLineMark.range(line.from));
          }
        }
        return Decoration.set(marks);
      }
    }
    return decorations;
  },
  provide: (f) => EditorView.decorations.from(f),
});

// Error line theme
const errorLineTheme = EditorView.baseTheme({
  '.cm-error-line': {
    backgroundColor: 'rgba(255, 80, 80, 0.15)',
  },
});

// Debounce delay for auto-compile (ms)
const AUTO_COMPILE_DELAY = 500;

export class ShaderEditor {
  constructor(container, options = {}) {
    this.container = container;
    this.defaultCode = options.defaultCode || '';
    this.onCompile = options.onCompile || (() => {});
    this.initialTheme = options.theme || 'oneDark';
    this.editorView = null;
    this.isLoaded = false;
    this.compileTimeout = null;
    this.isInitialLoad = true;
    this.themeCompartment = new Compartment();
  }

  async init() {
    try {
      // Custom keybindings
      const compileHandler = () => {
        this.compile();
        return true;
      };
      const customKeymap = keymap.of([
        { key: 'Mod-Enter', run: compileHandler },
        { key: 'Ctrl-Enter', run: compileHandler },
        indentWithTab,
      ]);

      const initialCode = this.defaultCode;

      // Create editor
      // Auto-compile on document change
      const autoCompileListener = EditorView.updateListener.of((update) => {
        if (update.docChanged && !this.isInitialLoad) {
          this.scheduleCompile();
        }
      });

      // Get initial theme extension
      const initialThemeExt = EDITOR_THEMES[this.initialTheme]?.extension || oneDark;

      const state = EditorState.create({
        doc: initialCode,
        extensions: [
          basicSetup,
          cpp(),
          this.themeCompartment.of(initialThemeExt),
          customKeymap,
          errorLinesField,
          errorLineTheme,
          autoCompileListener,
          EditorView.lineWrapping,
          EditorView.theme({
            '&': {
              fontSize: '13px',
            },
            '.cm-scroller': {
              overflow: 'auto',
              fontFamily: 'ui-monospace, "SF Mono", Menlo, Monaco, "Cascadia Code", monospace',
            },
            '.cm-content': {
              padding: '10px 0',
            },
          }),
        ],
      });

      this.editorView = new EditorView({
        state,
        parent: this.container,
      });

      this.isLoaded = true;
      return true;
    } catch (error) {
      console.error('Failed to load CodeMirror:', error);
      // Fallback to textarea
      this.createFallbackEditor();
      return false;
    }
  }

  createFallbackEditor() {
    const textarea = document.createElement('textarea');
    textarea.className = 'editor-fallback';
    textarea.value = this.defaultCode;
    textarea.spellcheck = false;
    this.container.appendChild(textarea);
    this.fallbackTextarea = textarea;
    this.isLoaded = true;
  }

  getCode() {
    if (this.editorView) {
      return this.editorView.state.doc.toString();
    } else if (this.fallbackTextarea) {
      return this.fallbackTextarea.value;
    }
    return this.defaultCode;
  }

  setCode(code, skipAutoCompile = true) {
    // Temporarily disable auto-compile for programmatic changes
    const wasInitialLoad = this.isInitialLoad;
    if (skipAutoCompile) {
      this.isInitialLoad = true;
    }

    if (this.editorView) {
      this.editorView.dispatch({
        changes: {
          from: 0,
          to: this.editorView.state.doc.length,
          insert: code,
        },
      });
    } else if (this.fallbackTextarea) {
      this.fallbackTextarea.value = code;
    }

    // Restore state after the dispatch
    if (skipAutoCompile) {
      this.isInitialLoad = wasInitialLoad;
    }
  }

  /**
   * Mark initialization as complete, enabling auto-compile
   */
  enableAutoCompile() {
    this.isInitialLoad = false;
  }

  /**
   * Schedule a compile with debounce
   */
  scheduleCompile() {
    if (this.compileTimeout) {
      clearTimeout(this.compileTimeout);
    }
    this.compileTimeout = setTimeout(() => {
      this.compile();
    }, AUTO_COMPILE_DELAY);
  }

  compile() {
    const code = this.getCode();
    this.onCompile(code);
  }

  reset() {
    this.setCode(this.defaultCode);
  }

  /**
   * Set error line highlights
   * @param {number[]} lines - Array of line numbers (1-indexed)
   */
  setErrorLines(lines) {
    if (this.editorView) {
      this.editorView.dispatch({
        effects: setErrorLinesEffect.of(lines),
      });
    }
  }

  /**
   * Clear all error highlights
   */
  clearErrors() {
    this.setErrorLines([]);
  }

  focus() {
    if (this.editorView) {
      this.editorView.focus();
    } else if (this.fallbackTextarea) {
      this.fallbackTextarea.focus();
    }
  }

  /**
   * Set editor theme
   * @param {string} themeId - Theme identifier (e.g., 'oneDark', 'light')
   */
  setTheme(themeId) {
    if (!this.editorView) return;

    const theme = EDITOR_THEMES[themeId];
    if (!theme) {
      console.warn(`Unknown theme: ${themeId}`);
      return;
    }

    this.editorView.dispatch({
      effects: this.themeCompartment.reconfigure(theme.extension),
    });
  }

  /**
   * Get available themes
   * @returns {Array} Array of {id, name} objects
   */
  static getThemes() {
    return Object.entries(EDITOR_THEMES).map(([id, { name }]) => ({ id, name }));
  }

  destroy() {
    if (this.compileTimeout) {
      clearTimeout(this.compileTimeout);
      this.compileTimeout = null;
    }
    if (this.editorView) {
      this.editorView.destroy();
      this.editorView = null;
    }
    if (this.fallbackTextarea) {
      this.fallbackTextarea.remove();
      this.fallbackTextarea = null;
    }
  }
}
